import {
  AdminCustomSelect,
  AdminOptionsDropdown,
  AdminSpinner,
  Disclaimer,
  Div,
  P,
  PlusIcon,
  StatusCircle,
  TooltipWrapper,
  lightTheme,
} from '@vgrubs/components';
import { useState } from 'react';
import { GearIcon } from '../../../assets/svg';
import { renderProviderIcon, renderProviderName } from '../../../helpers';
import { BrandMenuResponse } from '../../../types/api/menu.types';
import { BrandMenusState } from '../hooks/useBrandMenus';
import { MenuConfirmModal } from './MenuConfirmModal';
import { MenuFormModal } from './MenuFormModal';
import {
  MenuName,
  MenuPickerBar,
  MenuSelectWrap,
  NewMenuOptionPlus,
  ProviderIconRow,
} from './MenuPicker.styled';
import { MenuProvidersModal } from './MenuProvidersModal';

const MAX_ICONS = 4;

// Last option of the select: opens the create modal instead of selecting a
// menu. Menu ids are positive, so this can never collide.
const NEW_MENU_OPTION = -1;

const UNPUBLISHED_TOOLTIP =
  'Unpublished changes. Open this menu and press Publish to send them to its providers.';

// TooltipWrapper renders an inline-block div around an inline anchor, which
// sits on the text baseline and lifts the dot above centre.
const TOOLTIP_FLEX = { display: 'flex', alignItems: 'center' } as const;

const ProviderIcons = ({ menu }: { menu: BrandMenuResponse }) => (
  <ProviderIconRow
    title={`Published to: ${menu.providers
      .map((p) => renderProviderName(p))
      .join(', ')}`}
  >
    {menu.providers.slice(0, MAX_ICONS).map((p) => (
      <img key={p} src={renderProviderIcon(p)} alt="" />
    ))}
    {menu.providers.length > MAX_ICONS && (
      <Disclaimer $m="0 0 0 2px" $color={lightTheme.darkGray}>
        +{menu.providers.length - MAX_ICONS}
      </Disclaimer>
    )}
  </ProviderIconRow>
);

const UnpublishedDot = () => (
  <TooltipWrapper tooltip={UNPUBLISHED_TOOLTIP} style={TOOLTIP_FLEX}>
    <Div $flex $alignCenter>
      <StatusCircle $color={lightTheme.primary} />
    </Div>
  </TooltipWrapper>
);

/** One option per menu: dot when unpublished, name (default), providers. */
const menuOption = (menu: BrandMenuResponse) => {
  const name = menu.isDefault ? `${menu.name} (default)` : menu.name;
  return {
    name,
    value: menu.id,
    icon: menu.shouldPublish ? <UnpublishedDot /> : undefined,
    label: (
      <Div $flex $alignCenter $gap="8px" $minWidth="0">
        <MenuName>{name}</MenuName>
        {menu.providers.length > 0 && <ProviderIcons menu={menu} />}
      </Div>
    ),
  };
};

type Props = {
  brandMenus: BrandMenusState;
  locationId: string;
  brandId: string;
  isPos: boolean;
};

/**
 * The brand's menus above the Items / Modifiers / ... tabs: a select of the
 * menus (with "New Menu" as its last option) and the actions on the open one
 * (rename, default, delete, "menu per provider") in the dropdown next to it.
 * Renders nothing while the list is empty, which is also what a brand with
 * the multi-menu flag off (or a backend without multi-menu) looks like.
 */
export const MenuPicker = ({
  brandMenus,
  locationId,
  brandId,
  isPos,
}: Props) => {
  const {
    menus,
    selectedMenu,
    select,
    busy,
    reload,
    createMenu,
    renameMenu,
    setDefaultMenu,
    removeMenu,
  } = brandMenus;

  const [formMode, setFormMode] = useState<'create' | 'rename' | null>(null);
  const [confirm, setConfirm] = useState<'delete' | 'default' | null>(null);
  const [providersOpen, setProvidersOpen] = useState(false);

  if (menus.length === 0 || !selectedMenu) return null;

  const defaultMenu = menus.find((m) => m.isDefault) ?? null;

  const actions = [
    {
      label: 'New Menu',
      onClick: () => setFormMode('create'),
    },
    {
      label: 'Rename Menu',
      onClick: () => setFormMode('rename'),
    },
    {
      label: 'Set as Default',
      onClick: () => setConfirm('default'),
      show: !selectedMenu.isDefault,
    },
    {
      label: 'Menu per Provider',
      onClick: () => setProvidersOpen(true),
    },
    {
      label: 'Delete Menu',
      onClick: () => setConfirm('delete'),
      show: !selectedMenu.isDefault,
      color: lightTheme.primary,
    },
  ];

  const options = [
    ...menus.map(menuOption),
    {
      name: 'New Menu',
      value: NEW_MENU_OPTION,
      icon: (
        <NewMenuOptionPlus>
          <PlusIcon />
        </NewMenuOptionPlus>
      ),
    },
  ];

  return (
    <>
      {busy && <AdminSpinner />}

      <MenuPickerBar>
        <MenuSelectWrap>
          <AdminCustomSelect<number>
            value={selectedMenu.id}
            onChange={(value) => {
              if (value === NEW_MENU_OPTION) setFormMode('create');
              else if (typeof value === 'number') select(value);
            }}
            options={options}
          />
        </MenuSelectWrap>

        <Div $flexNone>
          <AdminOptionsDropdown
            actions={actions}
            trigger={<GearIcon color={lightTheme.darkGray} />}
          />
        </Div>
      </MenuPickerBar>

      <MenuFormModal
        isOpen={formMode !== null}
        mode={formMode ?? 'create'}
        closeModal={() => setFormMode(null)}
        menus={menus}
        current={formMode === 'rename' ? selectedMenu : null}
        isPos={isPos}
        onCreate={createMenu}
        onRename={renameMenu}
      />

      <MenuConfirmModal
        isOpen={confirm === 'default'}
        closeModal={() => setConfirm(null)}
        title={`Make "${selectedMenu.name}" the default menu?`}
        confirmLabel="Set as Default"
        onConfirm={() => setDefaultMenu(selectedMenu.id)}
      >
        <P $mb="16">
          Providers that follow the default menu
          {defaultMenu ? ` (currently "${defaultMenu.name}")` : ''} switch to{' '}
          <b>{selectedMenu.name}</b> and receive its last published version
          right away.
        </P>
        <P $mb="16">Providers with a menu of their own are not affected.</P>
      </MenuConfirmModal>

      <MenuConfirmModal
        isOpen={confirm === 'delete'}
        closeModal={() => setConfirm(null)}
        title={`Delete "${selectedMenu.name}"?`}
        confirmLabel="Delete Menu"
        onConfirm={() => removeMenu(selectedMenu.id)}
      >
        <P $mb="16">
          The menu is deleted together with its publish history and AI drafts.
          This cannot be undone.
        </P>
        <P $mb="16">
          {selectedMenu.providers.length > 0 ? (
            <>
              <b>
                {selectedMenu.providers
                  .map((p) => renderProviderName(p))
                  .join(', ')}
              </b>{' '}
              will switch to the default menu
              {defaultMenu ? ` ("${defaultMenu.name}")` : ''}, which is
              published to them right away.
            </>
          ) : (
            'No provider publishes this menu.'
          )}
        </P>
      </MenuConfirmModal>

      <MenuProvidersModal
        isOpen={providersOpen}
        closeModal={() => setProvidersOpen(false)}
        locationId={locationId}
        brandId={brandId}
        menus={menus}
        onSaved={reload}
      />
    </>
  );
};
